import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, MenuItem, TextField, Button } from '@mui/material'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import { intelColors, formatNumber } from './components/intelTheme'
import { intelApi } from './api'

const WINDOWS = [
  { value: 0, label: 'All time (backlog)' },
  { value: 1, label: 'Today' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
]
const SUBTABS = [
  { key: 'summary', label: '📊 Summary' },
  { key: 'client', label: '🏢 Client Losses' },
  { key: 'orders', label: '📋 Order Details' },
  { key: 'pins', label: '📍 PIN Codes' },
]
const PIN_SIZE = 50    // server page size for the pincode list (Summary + PIN Codes tabs)
const ORDER_SIZE = 25  // server page size for the order-detail list

const inrShort = (v) => {
  const n = Math.round(v || 0)
  if (n >= 1e7) return '₹' + (n / 1e7).toFixed(2) + 'Cr'
  if (n >= 1e5) return '₹' + (n / 1e5).toFixed(1) + 'L'
  return '₹' + formatNumber(n)
}
const inr = (v) => '₹' + formatNumber(Math.round(v || 0))
// stuck/loss: ▲ up = bad (red), ▼ down = good (green)
const lossDelta = (cur, prev) => {
  const d = (cur || 0) - (prev || 0)
  if (d > 0) return { text: `▲ +${formatNumber(d)} vs yesterday`, color: '#e53935' }
  if (d < 0) return { text: `▼ ${formatNumber(d)} vs yesterday`, color: '#43a047' }
  return { text: '— flat vs yesterday', color: '#888' }
}
const fmtTime = (ms) => {
  if (!ms) return '—'
  try {
    return new Date(ms).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  } catch (e) {
    return '—'
  }
}

const TabBtn = ({ active, onClick, children }) => (
  <Box
    component='button'
    onClick={onClick}
    sx={{
      padding: '7px 16px', border: `1px solid ${active ? intelColors.primary : intelColors.border}`,
      background: active ? intelColors.primary : '#fff', color: active ? '#fff' : '#555',
      cursor: 'pointer', fontSize: 12, fontWeight: 600, borderRadius: '6px', whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
)

export default function NonServiceable() {
  const [tab, setTab] = useState('summary')
  const [days, setDays] = useState(0)
  const [err, setErr] = useState(null)

  // Summary + PIN Codes share one fetch (pincode aggregation + KPIs) — server-paginated.
  const [pinData, setPinData] = useState(null)
  const [pinLoading, setPinLoading] = useState(false)
  const [pinPage, setPinPage] = useState(1)
  // Client losses
  const [clientData, setClientData] = useState(null)
  const [clientLoading, setClientLoading] = useState(false)
  // Order details (with filters) — server-paginated.
  const [orderData, setOrderData] = useState(null)
  const [orderLoading, setOrderLoading] = useState(false)
  const [orderPage, setOrderPage] = useState(1)
  const [draft, setDraft] = useState({ merchant: '', orderType: '', paymentMode: '' })
  const [applied, setApplied] = useState({ merchant: '', orderType: '', paymentMode: '' })

  const onErr = (e) => setErr(e?.response?.data?.exception || e.message)

  const loadPins = useCallback(() => {
    setPinLoading(true); setErr(null)
    intelApi.nonServiceable({ days, page: pinPage, size: PIN_SIZE }).then((res) => setPinData(res?.data?.responseObj || res?.data?.data || res?.data)).catch(onErr).finally(() => setPinLoading(false))
  }, [days, pinPage])
  const loadClients = useCallback(() => {
    setClientLoading(true); setErr(null)
    intelApi.nonServiceableClients({ days }).then((res) => setClientData(res?.data?.responseObj || res?.data?.data || res?.data)).catch(onErr).finally(() => setClientLoading(false))
  }, [days])
  const loadOrders = useCallback(() => {
    setOrderLoading(true); setErr(null)
    intelApi.nonServiceableOrders({ days, ...applied, page: orderPage, size: ORDER_SIZE }).then((res) => setOrderData(res?.data?.responseObj || res?.data?.data || res?.data)).catch(onErr).finally(() => setOrderLoading(false))
  }, [days, applied, orderPage])

  // Window change resets every tab's page; filter Apply/Reset resets the order page.
  const onWindow = (v) => { setDays(v); setPinPage(1); setOrderPage(1) }
  const applyFilters = () => { setApplied({ ...draft }); setOrderPage(1) }
  const resetFilters = () => { setDraft({ merchant: '', orderType: '', paymentMode: '' }); setApplied({ merchant: '', orderType: '', paymentMode: '' }); setOrderPage(1) }

  // Lazy-load the active tab; refetch on window change.
  useEffect(() => {
    if (tab === 'summary' || tab === 'pins') loadPins()
    else if (tab === 'client') loadClients()
    else if (tab === 'orders') loadOrders()
  }, [tab, loadPins, loadClients, loadOrders])

  const k = pinData || {}
  const ordD = lossDelta(k.ordersLostToday, k.ordersLostYesterday)
  const gmvDeltaTxt = useMemo(() => {
    const d = (k.gmvToday || 0) - (k.gmvYesterday || 0)
    if (d > 0) return { text: `▲ +${inrShort(d)} vs yesterday`, color: '#e53935' }
    if (d < 0) return { text: `▼ ${inrShort(Math.abs(d))} vs yesterday`, color: '#43a047' }
    return { text: '— flat vs yesterday', color: '#888' }
  }, [k.gmvToday, k.gmvYesterday]) // eslint-disable-line react-hooks/exhaustive-deps

  const pinRows = useMemo(() => pinData?.pincodes || [], [pinData])

  const summaryCols = [
    { key: 'pincode', header: 'Pincode', render: (r) => <strong>{r.pincode}</strong> },
    { key: 'city', header: 'City', render: (r) => r.city || '—' },
    { key: 'state', header: 'State', render: (r) => r.state || '—' },
    { key: 'zone', header: 'Zone', render: (r) => (r.zone ? <Badge color='grey'>{r.zone}</Badge> : '—') },
    { key: 'ordersLost', header: 'Orders Lost', render: (r) => formatNumber(r.ordersLost) },
    { key: 'gmvLost', header: 'GMV Lost', render: (r) => inr(r.gmvLost) },
    { key: 'lastServiceable', header: 'Last Serviceable', info: 'Last date this PIN appeared in a courier coverage upload (bulk_serviceability_snapshot). Blank = no snapshot history yet.', render: (r) => r.lastServiceable || '—' },
    { key: 'lastCourier', header: 'Last Courier', render: (r) => r.lastCourier || '—' },
    { key: 'topReason', header: 'Reason', render: (r) => (r.topReason ? <Badge color='orange'>{r.topReason}</Badge> : '—') },
    { key: 'daysOut', header: 'Days Out', render: (r) => (r.daysOut != null ? r.daysOut : '—') },
  ]
  const pinCols = [
    summaryCols[0], summaryCols[1], summaryCols[2], summaryCols[3], summaryCols[4], summaryCols[5],
    { key: 'codLost', header: 'COD Lost', render: (r) => formatNumber(r.codLost) },
    { key: 'prepaidLost', header: 'Prepaid Lost', render: (r) => formatNumber(r.prepaidLost) },
    summaryCols[6], summaryCols[7], summaryCols[8], summaryCols[9],
  ]

  const clientCols = [
    { key: 'merchantName', header: 'Client / Merchant', render: (r) => <strong>{r.merchantName || r.merchant}</strong> },
    { key: 'ordersToday', header: 'Orders Lost Today', render: (r) => formatNumber(r.ordersToday) },
    { key: 'gmvToday', header: 'GMV Lost Today', render: (r) => inr(r.gmvToday) },
    { key: 'codLost', header: 'COD Lost', render: (r) => formatNumber(r.codLost) },
    { key: 'prepaidLost', header: 'Prepaid Lost', render: (r) => formatNumber(r.prepaidLost) },
    { key: 'orders7d', header: 'Orders Lost (7d)', render: (r) => formatNumber(r.orders7d) },
    { key: 'gmv7d', header: 'GMV Lost (7d)', render: (r) => inr(r.gmv7d) },
    { key: 'topState', header: 'Top Impacted State', render: (r) => r.topState || '—' },
  ]

  const orderCols = [
    { key: 'orderId', header: 'Order ID', render: (r) => <code style={{ fontSize: 11 }}>{r.orderId}</code> },
    { key: 'merchantName', header: 'Merchant', render: (r) => r.merchantName || r.merchant },
    { key: 'orderType', header: 'Type', render: (r) => <Badge color={r.orderType === 'REVERSE' ? 'orange' : 'blue'}>{r.orderType}</Badge> },
    { key: 'paymentMode', header: 'Payment', render: (r) => <Badge color={r.paymentMode === 'COD' ? 'orange' : 'green'}>{r.paymentMode || '—'}</Badge> },
    { key: 'gmv', header: 'GMV', render: (r) => inr(r.gmv) },
    { key: 'weight', header: 'Wt (kg)', render: (r) => (r.weight != null ? r.weight : '—') },
    { key: 'originPin', header: 'Origin', render: (r) => r.originPin || '—' },
    { key: 'destPin', header: 'Dest PIN', render: (r) => <strong>{r.destPin || '—'}</strong> },
    { key: 'city', header: 'Dest City', render: (r) => r.city || '—' },
    { key: 'state', header: 'Dest State', render: (r) => r.state || '—' },
    { key: 'reason', header: 'Reason', render: (r) => (r.reason ? <Badge color='orange'>{r.reason}</Badge> : '—') },
    { key: 'couriersTried', header: 'Couriers Tried', render: (r) => <span style={{ fontSize: 11 }}>{r.couriersTried || '—'}</span> },
    { key: 'failureTime', header: 'Failure Time', render: (r) => fmtTime(r.failureTime) },
  ]

  return (
    <IntelLayout title='Pincode Coverage — Non-serviceable Orders'>
      {/* sub-tab bar + window */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {SUBTABS.map((t) => (
          <TabBtn key={t.key} active={tab === t.key} onClick={() => setTab(t.key)}>{t.label}</TabBtn>
        ))}
        <Box sx={{ flex: 1 }} />
        <TextField select label='Window' value={days} onChange={(e) => onWindow(Number(e.target.value))} size='small' sx={{ minWidth: 170 }}>
          {WINDOWS.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
        </TextField>
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>{err}</Box>
      )}

      {/* ── SUMMARY ── */}
      {tab === 'summary' && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '14px', mb: 2 }}>
            <KpiCard loading={pinLoading} color='red' label='Orders Lost Today' value={formatNumber(k.ordersLostToday)} sub={ordD.text} subColor={ordD.color} />
            <KpiCard loading={pinLoading} color='orange' label='GMV Lost Today' value={inrShort(k.gmvToday)} sub={gmvDeltaTxt.text} subColor={gmvDeltaTxt.color} />
            <KpiCard loading={pinLoading} color='orange' label='GMV Lost This Week' value={inrShort(k.gmvWeek)} sub='7-day rolling' />
            <KpiCard loading={pinLoading} color='red' label='GMV Lost This Month' value={inrShort(k.gmvMonth)} sub='30-day rolling' />
            <KpiCard loading={pinLoading} color='purple' label='Merchants Impacted' value={formatNumber(k.merchantsImpacted)} sub={`in ${days === 0 ? 'backlog' : 'window'}`} />
          </Box>
          <IntelTable
            title='Non-serviceable PIN Codes'
            action={<Badge color='red'>{`${formatNumber(k.pincodeCount)} pincodes`}</Badge>}
            loading={pinLoading} columns={summaryCols} rows={pinRows}
            rowKey={(r, i) => r.pincode || i}
            serverSide page={pinPage} totalRecords={k.pincodeCount} onPageChange={setPinPage} pageSize={PIN_SIZE}
            emptyMsg='No non-serviceable failures in this window'
          />
        </>
      )}

      {/* ── CLIENT LOSSES ── */}
      {tab === 'client' && (
        <IntelTable
          title='Business Loss by Client'
          loading={clientLoading} columns={clientCols} rows={clientData?.clients || []}
          rowKey={(r, i) => r.merchant || i} pageSize={50}
          emptyMsg='No client losses in this window'
        />
      )}

      {/* ── ORDER DETAILS ── */}
      {tab === 'orders' && (
        <>
          <Box sx={{ background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px', p: '12px 16px', mb: 2, display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <TextField label='Merchant ID' value={draft.merchant} onChange={(e) => setDraft({ ...draft, merchant: e.target.value })} size='small' sx={{ minWidth: 160 }} />
            <TextField select label='Order Type' value={draft.orderType} onChange={(e) => setDraft({ ...draft, orderType: e.target.value })} size='small' sx={{ minWidth: 130 }}>
              <MenuItem value=''>All</MenuItem><MenuItem value='FORWARD'>Forward</MenuItem><MenuItem value='REVERSE'>Reverse</MenuItem>
            </TextField>
            <TextField select label='Payment' value={draft.paymentMode} onChange={(e) => setDraft({ ...draft, paymentMode: e.target.value })} size='small' sx={{ minWidth: 120 }}>
              <MenuItem value=''>All</MenuItem><MenuItem value='COD'>COD</MenuItem><MenuItem value='PREPAID'>Prepaid</MenuItem>
            </TextField>
            <Button variant='contained' size='small' onClick={applyFilters}>Apply</Button>
            <Button variant='outlined' size='small' onClick={resetFilters}>Reset</Button>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', mb: 2 }}>
            <KpiCard loading={orderLoading} color='orange' label='COD Orders Lost' value={formatNumber(orderData?.codLost)} sub={pct(orderData?.codLost, orderData?.total)} />
            <KpiCard loading={orderLoading} color='green' label='Prepaid Orders Lost' value={formatNumber(orderData?.prepaidLost)} sub={pct(orderData?.prepaidLost, orderData?.total)} />
            <KpiCard loading={orderLoading} color='blue' label='Forward Orders Lost' value={formatNumber(orderData?.forwardLost)} sub={pct(orderData?.forwardLost, orderData?.total)} />
            <KpiCard loading={orderLoading} color='purple' label='Reverse/Exchange Lost' value={formatNumber(orderData?.reverseLost)} sub={pct(orderData?.reverseLost, orderData?.total)} />
          </Box>
          <IntelTable
            title='Failed Orders — Order-Level Detail'
            action={orderData ? <Badge color='grey'>{`${formatNumber(orderData.total)} orders`}</Badge> : null}
            loading={orderLoading} columns={orderCols} rows={orderData?.orders || []}
            rowKey={(r, i) => r.orderId || i}
            serverSide page={orderPage} totalRecords={orderData?.total || 0} onPageChange={setOrderPage} pageSize={ORDER_SIZE}
            emptyMsg='No failed orders match these filters'
          />
        </>
      )}

      {/* ── PIN CODES (full) ── */}
      {tab === 'pins' && (
        <>
          <IntelTable
            title='Non-serviceable PIN Codes — Full List'
            action={<Badge color='red'>{`${formatNumber(k.pincodeCount)} pincodes`}</Badge>}
            loading={pinLoading} columns={pinCols} rows={pinRows}
            rowKey={(r, i) => r.pincode || i}
            serverSide page={pinPage} totalRecords={k.pincodeCount} onPageChange={setPinPage} pageSize={PIN_SIZE}
            emptyMsg='No non-serviceable failures in this window'
          />
        </>
      )}
    </IntelLayout>
  )
}

const pct = (v, total) => {
  if (!total || v == null) return '—'
  return `${Math.round((v / total) * 100)}% of total`
}
